import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { useApi } from "../api.ts";
import { Button, cn, Icon, spot, type IconName } from "../ui/index.tsx";
import { LogoMark } from "../ui/logo.tsx";
import { DetectionBadge, GameArt } from "./_shared.tsx";
import type { Game, PublicStatus } from "../../shared/types.ts";

/** Fades children up the first time they scroll into view. */
function Reveal({ children, delay = 0, className }: { children: ReactNode; delay?: number; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e?.isIntersecting) { setSeen(true); io.disconnect(); } }, { rootMargin: "0px 0px -10% 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} className={cn(seen ? "animate-rise" : "opacity-0", className)} style={{ animationDelay: `${delay}ms` }}>{children}</div>
  );
}

function SectionHead({ kicker, title, body }: { kicker: string; title: ReactNode; body?: string }) {
  return (
    <Reveal className="mb-10 max-w-2xl">
      <div className="kicker mb-3 text-accent">{kicker}</div>
      <h2 className="text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl">{title}</h2>
      {body && <p className="mt-4 text-lg leading-relaxed text-muted">{body}</p>}
    </Reveal>
  );
}

// ── Hero ──

const LOG_LINES = ["handshake: exoware.app ok", "license: verified", "integrity: sha256 ok", "module: mapped", "overlay: attached"];

/** A stylised loader window: cycles through the operational games and loops an inject sequence. */
function LoaderMockup({ games }: { games: Game[] }) {
  const list = games.filter((g) => g.status === "operational").slice(0, 4);
  const [active, setActive] = useState(0);
  const [progress, setProgress] = useState(0);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (list.length === 0) return;
    const id = setInterval(() => {
      setProgress((p) => {
        if (p >= 100) { setActive((a) => (a + 1) % list.length); return 0; }
        return Math.min(100, p + 3 + Math.random() * 5);
      });
    }, 120);
    return () => clearInterval(id);
  }, [list.length]);

  const game = list[active];
  const step = Math.min(LOG_LINES.length, Math.floor(progress / 20) + 1);

  return (
    <div className="[perspective:1400px]"
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        setTilt({ x: ((e.clientY - r.top) / r.height - 0.5) * -8, y: ((e.clientX - r.left) / r.width - 0.5) * 10 });
      }}
      onMouseLeave={() => setTilt({ x: 0, y: 0 })}>
      <div className="pane hud hud-accent glow-border relative overflow-hidden bg-surface/90 backdrop-blur-xl transition-transform duration-300 ease-out"
        style={{ transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)` }}>
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <div className="flex items-center gap-2.5">
            <LogoMark className="size-5" />
            <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted">exoware // loader</span>
          </div>
          <div className="flex gap-1.5">{["bg-faint/40", "bg-faint/40", "bg-accent/80"].map((c, i) => <span key={i} className={`size-2.5 rounded-full ${c}`} />)}</div>
        </div>
        <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
          <ul className="space-y-1 border-r border-line p-2">
            {(list.length ? list : [null, null, null, null]).map((g, i) => (
              <li key={g?.id ?? i} className={cn("flex items-center gap-2.5 rounded-lg p-1.5 transition-all duration-300", i === active ? "bg-accent/[0.12] ring-1 ring-accent/40" : "opacity-55")}>
                {g ? <GameArt game={g} className="h-8 w-14 shrink-0 rounded-md" /> : <div className="h-8 w-14 rounded-md bg-raised" />}
                <span className="truncate text-xs font-medium">{g?.name ?? "…"}</span>
              </li>
            ))}
          </ul>
          <div className="flex min-h-56 flex-col p-4">
            <div className="kicker mb-1">target</div>
            <div className="truncate font-display text-lg font-semibold">{game?.name ?? "Standby"}</div>
            <div className="mt-1.5 h-5">{game && <DetectionBadge status={game.detection_status} />}</div>
            <div className="mt-3 space-y-0.5 font-mono text-[11px] leading-5 text-muted">
              {LOG_LINES.slice(0, step).map((l, i) => (
                <div key={l} className={i === step - 1 ? "text-fg" : undefined}>
                  <span className="text-accent">›</span> {l}
                  {i === step - 1 && <span className="ml-1 inline-block h-3 w-1.5 translate-y-0.5 bg-accent animate-blink" />}
                </div>
              ))}
            </div>
            <div className="mt-auto pt-4">
              <div className="mb-1.5 flex justify-between font-mono text-[10px] uppercase tracking-[0.16em] text-faint">
                <span>{progress >= 100 ? "ready" : "injecting"}</span><span className="tabular-nums text-fg">{Math.floor(progress)}%</span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-raised">
                <div className="h-full rounded-full bg-gradient-to-r from-accent to-ember shadow-[0_0_12px_var(--color-accent)] transition-[width] duration-150" style={{ width: `${progress}%` }} />
              </div>
            </div>
          </div>
        </div>
        <div className="pointer-events-none absolute inset-x-0 top-0 h-1/3 animate-scan bg-gradient-to-b from-transparent via-white/[0.03] to-transparent" />
      </div>
    </div>
  );
}

function avgUptime(s?: PublicStatus): string {
  const tracked = s?.games.map((g) => g.uptime).filter((u): u is number => u !== null) ?? [];
  return tracked.length ? `${(tracked.reduce((a, b) => a + b, 0) / tracked.length).toFixed(tracked.every((u) => u === 100) ? 0 : 1)}%` : "—";
}

function Hero({ games, status }: { games: Game[]; status?: PublicStatus }) {
  const undetected = games.filter((g) => g.detection_status === "undetected").length;
  const telemetry = [
    { label: "90d uptime", value: avgUptime(status), live: true },
    { label: "games live", value: String(games.filter((g) => g.status === "operational").length || "—") },
    { label: "undetected", value: undetected ? `${undetected}/${games.length}` : "—" },
  ];
  return (
    <section className="relative">
      <div className="mx-auto grid max-w-6xl items-center gap-14 px-6 pb-20 pt-14 sm:pt-20 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
        <div className="min-w-0">
          <div className="kicker mb-6 flex items-center gap-2 animate-rise">
            <span className="relative flex size-2"><span className="absolute inset-0 animate-ping rounded-full bg-ok/70" /><span className="relative size-2 rounded-full bg-ok" /></span>
            v2 live // all systems nominal
          </div>
          <h1 className="text-[3.4rem] font-bold uppercase leading-[0.92] tracking-tight sm:text-7xl lg:text-[5.4rem] animate-rise [animation-delay:80ms]">
            Your<br />unfair<br /><span className="text-gradient">advantage</span><span className="text-accent">.</span>
          </h1>
          <p className="mt-7 max-w-md text-lg leading-relaxed text-muted animate-rise [animation-delay:160ms]">
            ESP, aimbot and full game control across CS2, ARK, Minecraft and more. One loader, always current, patched the same day the game updates.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-3 animate-rise [animation-delay:240ms]">
            <Link to="/register"><Button variant="primary" size="lg" icon="zap" className="glow-border h-12 px-6">Get started</Button></Link>
            <Link to="/pricing"><Button size="lg" className="h-12 px-6">See pricing</Button></Link>
          </div>
          <dl className="mt-12 grid max-w-md grid-cols-3 divide-x divide-line rounded-xl border border-line bg-surface/50 backdrop-blur animate-rise [animation-delay:320ms]">
            {telemetry.map((t) => (
              <div key={t.label} className="px-4 py-3">
                <dt className="kicker flex items-center gap-1.5 text-[10px]">{t.live && <span className="size-1.5 rounded-full bg-ok animate-pulse-soft" />}{t.label}</dt>
                <dd className="mt-1 font-display text-2xl font-semibold tabular-nums">{t.value}</dd>
              </div>
            ))}
          </dl>
        </div>
        <div className="min-w-0 animate-rise [animation-delay:200ms]"><LoaderMockup games={games} /></div>
      </div>
    </section>
  );
}

// ── Marquee ──

function GameMarquee({ games }: { games: Game[] }) {
  if (games.length === 0) return null;
  const row = [...games, ...games];
  return (
    <section className="relative overflow-hidden border-y border-line/70 bg-surface/40 py-5 backdrop-blur [mask-image:linear-gradient(90deg,transparent,#000_10%,#000_90%,transparent)]">
      <div className="flex w-max animate-marquee gap-4 hover:[animation-play-state:paused]">
        {row.map((g, i) => (
          <Link key={`${g.id}-${i}`} to={`/games/${g.id}`} className="group flex items-center gap-3 rounded-xl border border-line bg-bg/60 py-2 pl-2 pr-4 transition-colors hover:border-accent/50">
            <GameArt game={g} className="h-9 w-16 rounded-lg [&_img]:transition-transform group-hover:[&_img]:scale-110" />
            <span className="whitespace-nowrap text-sm font-medium">{g.name}</span>
            <span className={cn("size-1.5 rounded-full", g.status === "operational" ? "bg-ok shadow-[0_0_8px_var(--color-ok)]" : g.status === "offline" ? "bg-bad" : "bg-info")} />
          </Link>
        ))}
      </div>
    </section>
  );
}

// ── Bento ──

function Tile({ icon, title, body, className, children }: { icon: IconName; title: string; body: string; className?: string; children?: ReactNode }) {
  return (
    <div onMouseMove={spot} className={cn("pane hud spotlight group flex flex-col overflow-hidden p-6 transition-colors hover:border-line-strong", className)}>
      <div className="mb-5 grid size-10 place-items-center rounded-xl bg-accent-soft text-accent ring-1 ring-accent/20 transition-transform group-hover:scale-110">
        <Icon name={icon} className="size-5" />
      </div>
      <h3 className="text-xl font-semibold">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-muted">{body}</p>
      {children}
    </div>
  );
}

function Bento({ games }: { games: Game[] }) {
  return (
    <section className="mx-auto max-w-6xl px-6 py-24">
      <SectionHead kicker="// why exoware" title={<>Built like a weapon.<br /><span className="text-muted">Feels like a feature.</span></>} />
      <div className="grid gap-4 md:grid-cols-6">
        <Reveal className="md:col-span-4 md:row-span-2">
          <Tile icon="shield" title="Stays undetected" className="h-full"
            body="We watch every anti-cheat and push fixes before bans happen. Each game shows its live detection status, straight from our servers.">
            <div className="relative mt-6 flex min-h-72 flex-1 items-center justify-center">
              <div className="absolute size-72 rounded-full border border-accent/10" />
              <div className="absolute size-52 rounded-full border border-accent/15" />
              <div className="absolute size-32 rounded-full border border-accent/25" />
              <LogoMark framed={false} className="size-48 drop-shadow-[0_0_40px_rgb(255_61_79/0.45)]" />
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {games.filter((g) => g.status === "operational").slice(0, 4).map((g) => (
                <span key={g.id} className="flex items-center gap-2 rounded-lg border border-line bg-bg/70 px-2 py-1 text-xs backdrop-blur">{g.name}<DetectionBadge status={g.detection_status} /></span>
              ))}
            </div>
          </Tile>
        </Reveal>
        <Reveal delay={80} className="md:col-span-2"><Tile icon="zap" title="Same-day patches" className="h-full" body="Game updates? We patch the same day and the loader pulls the new build by itself." /></Reveal>
        <Reveal delay={160} className="md:col-span-2"><Tile icon="gamepad" title="One loader" className="h-full" body="Every game on your account in one app. Switch titles without reinstalling anything." /></Reveal>
        <Reveal delay={80} className="md:col-span-2"><Tile icon="monitor" title="Self-serve HWID reset" className="h-full" body="New PC? Reset your hardware lock from the dashboard. No ticket, no fee." /></Reveal>
        <Reveal delay={160} className="md:col-span-2"><Tile icon="lifebuoy" title="Real humans" className="h-full" body="Tickets answered fast, plus setup guides and an error-code reference." /></Reveal>
        <Reveal delay={240} className="md:col-span-2"><Tile icon="activity" title="Live telemetry" className="h-full" body="Status and detection come from the servers in real time, not a stale marketing page." /></Reveal>
      </div>
    </section>
  );
}

// ── Steps + CTA ──

const STEPS = [
  { title: "Create an account", body: "A username and a password. No email needed." },
  { title: "Pick your game", body: "One day to lifetime. Access lands on your account straight away." },
  { title: "Launch and play", body: "Sign into the loader, pick the game, go. Most people are in-game in two minutes." },
];

function Steps() {
  return (
    <section className="mx-auto max-w-6xl px-6 py-16">
      <SectionHead kicker="// deploy sequence" title="In-game in under two minutes" />
      <ol className="relative grid gap-8 md:grid-cols-3">
        <div className="absolute left-0 right-0 top-6 hidden h-px bg-gradient-to-r from-accent/60 via-line to-transparent md:block" />
        {STEPS.map((s, i) => (
          <li key={s.title} className="relative">
            <Reveal delay={i * 120}>
              <div className="relative z-10 mb-5 grid size-12 place-items-center rounded-xl border border-accent/40 bg-bg font-display text-lg font-bold text-accent shadow-[0_0_24px_-6px_var(--color-accent)]">
                0{i + 1}
              </div>
              <h3 className="text-xl font-semibold">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted">{s.body}</p>
            </Reveal>
          </li>
        ))}
      </ol>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="mx-auto max-w-6xl px-6 py-20">
      <Reveal>
        <div className="pane hud hud-accent glow-border relative overflow-hidden px-8 py-16 text-center sm:py-20">
          <div className="pointer-events-none absolute left-1/2 top-1/2 h-72 w-[44rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent/20 blur-[110px]" />
          <LogoMark className="relative mx-auto mb-8 size-20 drop-shadow-[0_0_40px_rgb(255_61_79/0.55)]" />
          <h2 className="relative text-5xl font-bold uppercase leading-none tracking-tight sm:text-6xl">Play <span className="text-gradient">different</span>.</h2>
          <p className="relative mx-auto mt-5 max-w-md text-lg text-muted">Two minutes from now you could be in a match. Make the account.</p>
          <div className="relative mt-9 flex flex-wrap justify-center gap-3">
            <Link to="/register"><Button variant="primary" size="lg" className="h-12 px-7">Create your account</Button></Link>
            <Link to="/games"><Button size="lg" className="h-12 px-7">Browse games</Button></Link>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

export default function Landing() {
  const games = useApi<Game[]>("/games");
  const status = useApi<PublicStatus>("/status");
  const list = games.data ?? [];
  return (
    <>
      <Hero games={list} status={status.data} />
      <GameMarquee games={list} />
      <Bento games={list} />
      <Steps />
      <FinalCta />
    </>
  );
}
